import { getAccessToken } from "./authToken";

// WebSocketService.ts
export interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

export interface WebSocketConfig {
  wsUrl: string;
  apiUrl?: string; // Add API URL for session creation
  sessionId?: string; 
  userId: string;
  profileName: string;
  userEmail?: string; 
  role?: string;       
  roleType?: string;   
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
  heartbeatInterval?: number;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'initializing';

const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectAttempts = 0;
  private reconnectTimeout: any = null;
  private heartbeatInterval: any = null;
  private lastMessageTime: Date = new Date();
  private messageQueue: any[] = [];
  private isStarted = false;
  private isClosing = false;
  private clickedClose = false;
  
  // Callbacks
  private onMessageReceived?: (message: Message) => void;
  private onConnectionStatusChange?: (status: ConnectionStatus) => void;
  private onTypingStatusChange?: (isTyping: boolean) => void;
  private onError?: (error: any) => void;
  private onSessionChanged?: (sessionId: string) => void;

  // const hostname = window.location.a
  
  constructor(config: WebSocketConfig) 
  {
    const hostname = window.location.hostname;
    let baseurl = '';
    if (hostname.includes('localhost')) {
      baseurl = 'http://localhost:5000';
    } else if (hostname.includes('dev')) {
      baseurl= 'https://candidateinterviewscheduler-dev.pwc.com';
    } else if (hostname.includes('stg') || hostname.includes('stage')) {
      baseurl = 'https://candidateinterviewscheduler-stg.pwc.com';
    } else {
      baseurl = 'https://candidateinterviewscheduler.pwc.com'; // fallback production
    }

    this.config = {
      maxReconnectAttempts: 10,
      reconnectDelay: 1000,
      heartbeatInterval: 30000,
      apiUrl: `${baseurl}`, // Default API URL
      ...config
    };
  }

  // Generate UUID v4 (backup method if backend fails)
  private generateUUID(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Create session via backend API
  // In createBackendSession method
private async createBackendSession(): Promise<string | null> {
    try {
      const token = await getAccessToken();
        if (!token) {
          throw new Error('Authentication required - please log in');
        }

      console.log('Creating session via backend API...');
      const response = await fetch(`${this.config.apiUrl}/ws/session`, {
        method: 'POST',
        credentials: 'include',  // This is crucial
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.status}`);
      }

      const data = await response.json();
      console.log('Session creation response:', data);
      
      // Check cookies after creation
      console.log('Document cookies:', document.cookie);
      
      // Return the session_id from response if available
      if (data.session_id) {
        return data.session_id;
      }
      
      // Try to extract from cookies as fallback
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [key, value] = cookie.trim().split('=');
        if (key === 'session_id') {
          console.log('Session cookie found:', value);
          return value;
        }
      }
      
      console.warn('Session created but cookie not accessible (might be httpOnly)');
      return 'backend-managed';
      
    } catch (error) {
      console.error('Failed to create backend session:', error);
      return null;
    }
  }

  private storeSession(sessionId: string): void {
    if (sessionId && sessionId !== 'backend-managed') {
      sessionStorage.setItem('websocket_session_id', sessionId);
      sessionStorage.setItem('websocket_session_timestamp', new Date().toISOString());
    }
  }

  private clearStoredSession(): void {
    sessionStorage.removeItem('websocket_session_id');
    sessionStorage.removeItem('websocket_session_timestamp');
  }

  public getSessionId(): string | undefined {
    return this.config.sessionId;
  }

  public getUserId(): string {
    return this.config.userId;
  }

  public getProfileName(): string {
    return this.config.profileName;
  }

  public getUserEmail(): string | undefined {
    return this.config.userEmail;
  }
  
  public getRole(): string | undefined {
    return this.config.role;
  }
  
  public getRoleType(): string | undefined {
    return this.config.roleType;
  }

  public async refreshSession(): Promise<void> {
    console.log('Refreshing session...');
    
    this.clearStoredSession();
    
    // Create new session via backend
    const newSessionId = await this.createBackendSession();
    
    if (newSessionId) {
      this.config.sessionId = newSessionId;
      this.storeSession(newSessionId);
    } else {
      // Fallback to client-side UUID if backend fails
      const fallbackId = this.generateUUID();
      this.config.sessionId = fallbackId;
      this.storeSession(fallbackId);
      console.warn('Using fallback client-side session ID');
    }
    
    this.isStarted = false;
    this.messageQueue = [];
    
    if (this.onSessionChanged && this.config.sessionId) {
      this.onSessionChanged(this.config.sessionId);
    }
    
    this.reconnect(true);
    
    console.log('Session refreshed:', this.config.sessionId);
  }

  // Event handler setters
  public onMessage(callback: (message: Message) => void) {
    this.onMessageReceived = callback;
  }

  public onStatusChange(callback: (status: ConnectionStatus) => void) {
    this.onConnectionStatusChange = callback;
  }

  public onTypingChange(callback: (isTyping: boolean) => void) {
    this.onTypingStatusChange = callback;
  }

  public onErrorHandler(callback: (error: any) => void) {
    this.onError = callback;
  }

  public onSessionChange(callback: (sessionId: string) => void) {
    this.onSessionChanged = callback;
  }

  public async connect(): Promise<void> {
      this.clickedClose = false;
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        return;
      }

      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        console.log('Connection already in progress');
        return;
      }

      this.cleanup();
      this.isClosing = false;
      
      // ALWAYS create session via backend first
      this.updateConnectionStatus('initializing');
      
      try {
        // Create session and wait for it to complete
        const sessionId = await this.createBackendSession();
        
        if (!sessionId) {
          throw new Error('Failed to create backend session');
        }
        
        this.config.sessionId = sessionId;
        this.storeSession(sessionId);
        console.log('Backend session created successfully:', sessionId);
        
        // Add a small delay to ensure cookie is set
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        console.error('Session creation failed:', error);
        this.updateConnectionStatus('disconnected');
        this.handleError(error);
        return; // Don't attempt WebSocket connection without session
      }
      
      console.log(`Connecting to WebSocket... (attempt ${this.reconnectAttempts + 1})`);
      this.updateConnectionStatus('connecting');

      try {
        console.log(`Connecting to: ${this.config.wsUrl}`);
        this.ws = new WebSocket(this.config.wsUrl);
        this.setupWebSocketHandlers();
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        this.updateConnectionStatus('disconnected');
        this.handleError(error);
      }
    }
  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    const connectionTimeout = setTimeout(() => {
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        console.log('Connection timeout, closing...');
        this.ws.close();
      }
    }, 10000);

    this.ws.onopen = () => {
      clearTimeout(connectionTimeout);
      console.log('WebSocket connected successfully');
      this.updateConnectionStatus('connected');
      
      this.reconnectAttempts = 0;
      this.lastMessageTime = new Date();
      
      this.startHeartbeat();
      this.processMessageQueue();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleWebSocketMessage(data);
      } catch (error) {
        console.error('WebSocket message parse error:', error);
        this.handleError(error);
      }
    };

    this.ws.onerror = (error) => {
      clearTimeout(connectionTimeout);
      console.error('WebSocket error:', error);
      this.updateConnectionStatus('disconnected');
      this.updateTypingStatus(false);
      this.handleError(error);
    };

    this.ws.onclose = (event) => {
      clearTimeout(connectionTimeout);
      console.log(`WebSocket closed. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}`);
      
      this.updateConnectionStatus('disconnected');
      this.updateTypingStatus(false);
      this.stopHeartbeat();
      
      if (!this.isClosing) {
        this.scheduleReconnect(event.code);
      } else {
        console.log('WebSocket closed intentionally');
      }
    };
  }

  private handleWebSocketMessage(data: any): void {
    console.log('Received:', data);
    this.lastMessageTime = new Date();

    const messageType = (data.type || '').toLowerCase();

    if (messageType === 'heartbeat' || messageType === 'pong') {
      return;
    }
    
    if (messageType === 'ping') {
      this.sendPong();
      return;
    }

    if (messageType !== 'ready' && messageType !== 'greeting') {
      this.updateTypingStatus(false);
    }

    const newMessage: Message = {
      id: this.generateUUID(),
      content: '',
      isUser: false,
      timestamp: new Date()
    };

    switch (messageType) {
      case 'message':
      case 'response':
        newMessage.content = data.content || data.message || '';
        break;
      case 'error':
        newMessage.content = data.message || data.error || 'An error occurred';
        break;
      default:
        newMessage.content = data.content || data.message || '';
        if (!newMessage.content) {
          return;
        }
    }

    if (newMessage.content && newMessage.content.trim()) {
      this.handleIncomingMessage(newMessage);
    }
  }

  public sendMessage(content: string): void {
    if (!content.trim()) {
      console.warn('Cannot send empty message');
      return;
    }

    const userMessage: Message = {
      id: this.generateUUID(),
      content: content,
      isUser: true,
      timestamp: new Date()
    };
    this.handleIncomingMessage(userMessage);
    
    this.updateTypingStatus(true);

    const messageData = {
      type: 'message',
      content: content,
      session_id: this.config.sessionId,
      user_id: this.config.userId,
      user_name: this.config.profileName,
      user_email: this.config.userEmail,
      role: this.config.role,           
      role_type: this.config.roleType,  
      agent_type: 'chat',
      is_first_message: !this.isStarted,
      timezone: USER_TIMEZONE
    };

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('Sending message:', content);
      this.ws.send(JSON.stringify(messageData));
    } else {
      console.log('WebSocket not ready, queueing message');
      this.messageQueue.push(messageData);
      this.reconnectAttempts = 0;
      this.connect();
    }

    if (!this.isStarted) {
      this.isStarted = true;
    }
  }

  public async disconnect(): Promise<void> {
    console.log('Disconnecting WebSocket...');
    this.isClosing = true;
    this.clickedClose = true;
    this.cleanup();
    
    if (this.ws) {
      this.ws.close(1000, 'User closed chat');
      this.ws = null;
    }
    
    this.updateConnectionStatus('disconnected');
    
    // Clear session and prepare for next connection
    this.clearStoredSession();
    
    // Create new session for next connection via backend
    const newSessionId = await this.createBackendSession();
    if (newSessionId) {
      this.config.sessionId = newSessionId;
      this.storeSession(newSessionId);
      console.log('Pre-created session for next connection:', newSessionId);
    } else {
      // Clear session ID to force creation on next connect
      this.config.sessionId = undefined;
    }
    
    if (this.onSessionChanged && this.config.sessionId) {
      this.onSessionChanged(this.config.sessionId);
    }
    
    this.isStarted = false;
    this.messageQueue = [];
    this.reconnectAttempts = 0;
  }

  private reconnect(forceNewSession: boolean = false): void {
    this.isClosing = true;
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.cleanup();
    
    if (forceNewSession) {
      this.isStarted = false;
      this.messageQueue = [];
    }
    
    setTimeout(() => {
      this.isClosing = false;
      this.connect();
    }, 100);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const now = new Date();
        const timeSinceLastMessage = now.getTime() - this.lastMessageTime.getTime();
        
        if (timeSinceLastMessage > 60000) {
          console.log('No response for 60 seconds, reconnecting...');
          this.ws.close();
        } else {
          console.log('Sending heartbeat...');
          this.ws.send(JSON.stringify({ 
            type: 'heartbeat', 
            timestamp: now.toISOString() 
          }));
        }
      }
    }, this.config.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private sendPong(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'pong' }));
    }
  }

  public setClickedClosed(value: boolean): void {
    this.clickedClose = value;
  }

  private scheduleReconnect(closeCode: number): void {
    if (this.isClosing || this.clickedClose) {
      console.log('Not scheduling reconnect - connection was closed intentionally');
      return;
    }
    
    if (this.reconnectAttempts >= (this.config.maxReconnectAttempts || 10)) {
      console.log('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const baseDelay = closeCode === 1006 ? 3000 : this.config.reconnectDelay || 1000;
    const delay = Math.min(
      baseDelay * Math.pow(1.5, this.reconnectAttempts - 1), 
      30000
    );
    
    console.log(`Reconnecting in ${delay}ms... (attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`);
    
    this.reconnectTimeout = setTimeout(() => {
      if (!this.isClosing) {
        this.connect();
      }
    }, delay);
  }

  private processMessageQueue(): void {
    setTimeout(() => {
      while (this.messageQueue.length > 0) {
        const queuedMessage = this.messageQueue.shift();
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify(queuedMessage));
        }
      }
    }, 1000);
  }

  private cleanup(): void {
    this.stopHeartbeat();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private handleIncomingMessage(message: Message): void {
    if (this.onMessageReceived) {
      this.onMessageReceived(message);
    }
  }

  private updateConnectionStatus(status: ConnectionStatus): void {
    if (this.onConnectionStatusChange) {
      this.onConnectionStatusChange(status);
    }
  }

  private updateTypingStatus(isTyping: boolean): void {
    if (this.onTypingStatusChange) {
      this.onTypingStatusChange(isTyping);
    }
  }

  private handleError(error: any): void {
    if (this.onError) {
      this.onError(error);
    }
  }

  public getConnectionStatus(): ConnectionStatus {
    if (!this.ws) return 'disconnected';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      default: return 'disconnected';
    }
  }

  public isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}